import { CreditCard, Users, Sparkles, Building2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Country, CountryCode } from "@/lib/ecom/catalog";

export type PlanRow = {
  key: "free" | "pro_ai" | "teams" | "enterprise";
  name: string;
  icon: LucideIcon;
  tagline: string;
  /** undefined => no team/seat */
  perSeat?: boolean;
  cta: string;
  popular?: boolean;
  features: string[];
};

export const PLANS: PlanRow[] = [
  {
    key: "free",
    name: "Free",
    icon: CreditCard,
    tagline: "All the essentials to start.",
    cta: "Get started",
    features: [
      "1 digital business card",
      "Unlimited card taps & shares",
      "QR + Wallet card",
      "Basic contacts CRM (250)",
      "1 booking page",
      "Email + chat support",
    ],
  },
  {
    key: "pro_ai",
    name: "Pro AI",
    icon: Sparkles,
    tagline: "Everything you need to grow — solo.",
    cta: "14 Days Free Trial",
    popular: true,
    features: [
      "Everything in Free, plus:",
      "AI Brain — memory, scans & enrichment",
      "AI email drafts (200 / month)",
      "Unlimited contacts & pipelines",
      "Bookings with payments",
      "Contracts & e-signature (10 / mo)",
      "Automations & workflows",
      "Advanced analytics",
    ],
  },
  {
    key: "teams",
    name: "Teams",
    icon: Users,
    tagline: "For modern sales & operations teams.",
    perSeat: true,
    cta: "14 Days Free Trial",
    features: [
      "Everything in Pro AI, plus:",
      "Shared workspaces & roles",
      "Team-branded digital cards",
      "Shared CRM & pipelines",
      "Bulk NFC card ordering",
      "Admin console & SSO add-on",
      "Priority support",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    icon: Building2,
    tagline: "For organizations that need the works.",
    cta: "Talk to sales",
    features: [
      "Everything in Teams, plus:",
      "SSO / SCIM provisioning",
      "Custom contracts & DPA",
      "Dedicated success manager",
      "Custom integrations & API",
      "99.9% SLA & advanced security",
      "Onboarding & training",
    ],
  },
];

export const PLANS_VI: PlanRow[] = [
  {
    key: "free",
    name: "Miễn phí",
    icon: CreditCard,
    tagline: "Tất cả những gì cần thiết để bắt đầu.",
    cta: "Bắt đầu",
    features: [
      "1 danh thiếp kỹ thuật số",
      "Chạm & chia sẻ thẻ không giới hạn",
      "Thẻ QR + Wallet",
      "CRM liên hệ cơ bản (250)",
      "1 trang đặt lịch",
      "Hỗ trợ email + chat",
    ],
  },
  {
    key: "pro_ai",
    name: "Pro AI",
    icon: Sparkles,
    tagline: "Mọi thứ bạn cần để phát triển — cá nhân.",
    cta: "Dùng thử miễn phí",
    popular: true,
    features: [
      "Mọi tính năng của Miễn phí, cộng thêm:",
      "AI Brain — bộ nhớ, quét & làm giàu dữ liệu",
      "Soạn email bằng AI (200 / tháng)",
      "Liên hệ & pipeline không giới hạn",
      "Đặt lịch kèm thanh toán",
      "Hợp đồng & chữ ký điện tử (10 / tháng)",
      "Tự động hóa & quy trình",
      "Phân tích nâng cao",
    ],
  },
  {
    key: "teams",
    name: "Đội nhóm",
    icon: Users,
    tagline: "Dành cho đội ngũ bán hàng & vận hành hiện đại.",
    perSeat: true,
    cta: "Dùng thử cho đội",
    features: [
      "Mọi tính năng của Pro AI, cộng thêm:",
      "Workspace & phân quyền dùng chung",
      "Thẻ kỹ thuật số có thương hiệu đội",
      "CRM & pipeline dùng chung",
      "Đặt thẻ NFC số lượng lớn",
      "Bảng điều khiển quản trị & SSO add-on",
      "Hỗ trợ ưu tiên",
    ],
  },
  {
    key: "enterprise",
    name: "Doanh nghiệp",
    icon: Building2,
    tagline: "Dành cho tổ chức cần đầy đủ mọi thứ.",
    cta: "Liên hệ bán hàng",
    features: [
      "Mọi tính năng của Đội nhóm, cộng thêm:",
      "Cấp phát SSO / SCIM",
      "Hợp đồng & DPA tùy chỉnh",
      "Quản lý thành công chuyên trách",
      "Tích hợp & API tùy chỉnh",
      "SLA 99.9% & bảo mật nâng cao",
      "Onboarding & đào tạo",
    ],
  },
];

/**
 * Plan pricing per supported country, mirrored from the CRM project
 * (`src/lib/billing-pricing.ts`). Amounts are in MAJOR units of each
 * country's currency (dollars/euros/dong). SG and AE are approximated
 * from the USD table since the CRM only quotes 5 currencies natively.
 */
type PaidPlanKey = "pro_ai" | "teams";
type Interval = "month" | "year";

type PlanPriceEntry = { monthly: number; annual: number };
type PlanPriceTable = Record<PaidPlanKey, Record<CountryCode, PlanPriceEntry>>;

export const PLAN_PRICES: PlanPriceTable = {
  pro_ai: {
    US: { monthly: 16, annual: 12 * 12 },
    GB: { monthly: 12, annual: 9 * 12 },
    DE: { monthly: 14, annual: 10 * 12 },
    FR: { monthly: 14, annual: 10 * 12 },
    IT: { monthly: 14, annual: 10 * 12 },
    ES: { monthly: 14, annual: 10 * 12 },
    GR: { monthly: 14, annual: 10 * 12 },
    AU: { monthly: 23, annual: 17 * 12 },
    SG: { monthly: 20, annual: 16 * 12 },
    AE: { monthly: 60, annual: 45 * 12 },
    VN: { monthly: 399_000, annual: 149_000 * 12 * 2 },
  },
  teams: {
    US: { monthly: 12, annual: 9 * 12 },
    GB: { monthly: 9, annual: 7 * 12 },
    DE: { monthly: 10, annual: 8 * 12 },
    FR: { monthly: 10, annual: 8 * 12 },
    IT: { monthly: 10, annual: 8 * 12 },
    ES: { monthly: 10, annual: 8 * 12 },
    GR: { monthly: 10, annual: 8 * 12 },
    AU: { monthly: 17, annual: 13 * 12 },
    SG: { monthly: 16, annual: 12 * 12 },
    AE: { monthly: 45, annual: 35 * 12 },
    VN: { monthly: 299_000, annual: 99_000 * 12 * 2 },
  },
};

/** Monthly-equivalent major-unit amount for a plan/interval in a country. */
export function getPlanMonthly(
  planKey: PaidPlanKey,
  countryCode: CountryCode,
  interval: Interval,
): number {
  const entry = PLAN_PRICES[planKey][countryCode] ?? PLAN_PRICES[planKey].US;
  return interval === "year" ? entry.annual / 12 : entry.monthly;
}

const ZERO_DECIMAL = new Set(["VND", "JPY", "KRW"]);

/** Format a major-unit amount using the country's currency + locale. */
export function formatPlanPrice(country: Country, amount: number): string {
  const isZeroDecimal = ZERO_DECIMAL.has(country.currency);

  if (isZeroDecimal) {
    const rounded = Math.round(amount / 1000) * 1000;
    try {
      return new Intl.NumberFormat(country.locale, {
        style: "currency",
        currency: country.currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(rounded);
    } catch {
      return `${country.symbol}${rounded.toLocaleString()}`;
    }
  }

  // Show exact amounts, preserving cents only when the price has them.
  const rounded = Math.round(amount * 100) / 100;
  const hasDecimals = rounded !== Math.floor(rounded);
  try {
    return new Intl.NumberFormat(country.locale, {
      style: "currency",
      currency: country.currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: hasDecimals ? 2 : 0,
    }).format(rounded);
  } catch {
    return `${country.symbol}${rounded.toLocaleString(undefined, {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    })}`;
  }
}

/**
 * Static, hydration-safe default price string for a paid plan (US/USD).
 * Used as initial render text so a real price shows before the client
 * hook swaps in the user's selected currency.
 */
export function defaultPlanPriceText(planKey: PaidPlanKey, interval: Interval): string {
  const amount = getPlanMonthly(planKey, "US", interval);
  return `$${Math.round(amount).toLocaleString("en-US")}`;
}
